import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

import {
  useEventPlaces,
  useEventWriters,
} from "../../../../shared/data-access/calendar-data.provider";
import { useAnniversaryDateEvents } from "../../../../shared/hooks/use-date-anniversary-events";
import { useSelectedDate } from "../../../../shared/hooks/use-selected-date";
import { Event } from "../../../../shared/types/event-type";
import { Place } from "../../../../shared/types/place-type";
import { Theme } from "../../../../shared/types/theme-type";
import { Writer } from "../../../../shared/types/writer-type";
import {
  StyledLeftContainer,
  StyledRightContainer,
  StyledTableCell,
} from "./table-cell.style";

export const TableCell = ({ date }: { date: Dayjs }) => {
  const theme = useTheme();
  const selectedDate = useSelectedDate();
  const navigate = useNavigate();

  const [annivarsaryEvent] = sortEventsByPriority(
    useAnniversaryDateEvents(date)
  );

  const [writer] = useEventWriters(annivarsaryEvent?.id);
  const [place] = useEventPlaces(annivarsaryEvent?.id);

  const today = dayjs().format("DD/MM/YYYY");
  const isToday = date.format("DD/MM/YYYY") === today;
  const isCurrentMonth = date.month() === selectedDate.month();

  const { backgroundColor, fontColor } = getStyleValues(
    date,
    selectedDate,
    theme
  );

  if (isCurrentMonth && annivarsaryEvent) {
    const onCellClick = () => {
      const pathName: string = date
        ? `/date-events/?date=${JSON.stringify(date)}`
        : "/date-events";

      navigate(pathName);
    };

    return (
      <StyledTableCell
        isToday={isToday}
        areThereEvents={true}
        color={fontColor}
        backgroundColor={backgroundColor}
        onClick={onCellClick}
      >
        {writer ? (
          <TableCellWriter date={date} writer={writer} fontColor={fontColor} />
        ) : place ? (
          <TableCellPlace date={date} place={place} />
        ) : (
          <TableCellEvent date={date} event={annivarsaryEvent} />
        )}
      </StyledTableCell>
    );
  }

  return (
    <StyledTableCell
      isToday={isToday}
      areThereEvents={false}
      color={fontColor}
      backgroundColor={backgroundColor}
    >
      <StyledRightContainer>
        <span id="number">{date.date()}</span>
      </StyledRightContainer>
    </StyledTableCell>
  );
};

const sortEventsByPriority = (events: Event[]) => {
  const priorityToNumberMap: { [key: string]: number } = {
    low: 0,
    middle: 1,
    high: 2,
  };
  return events.sort(
    (a, b) => priorityToNumberMap[b.priority] - priorityToNumberMap[a.priority]
  );
};

const getStyleValues = (
  cellDate: Dayjs,
  selectedDate: Dayjs,
  theme: Theme
): { backgroundColor: string; fontColor: string } => {
  const { backgroundColors, fontColors } = theme.calendar;

  const isCurrentMonth = cellDate.month() === selectedDate.month();

  const backgroundColor = isCurrentMonth
    ? backgroundColors.mainCell
    : backgroundColors.emptyCell;
  const fontColor = isCurrentMonth
    ? cellDate.day() === 0
      ? fontColors.sunday
      : fontColors.main
    : fontColors.empty;

  return {
    backgroundColor,
    fontColor,
  };
};

type TableCellWriterProps = {
  date: Dayjs;
  writer: Writer;
  fontColor: any;
};

const TableCellWriter = ({ date, writer, fontColor }: TableCellWriterProps) => {
  const { fullName, imagePath, birthDate, deathDate } = writer;
  const birthYear: number = dayjs(birthDate).year();
  const deathYear: number | string = deathDate
    ? dayjs(deathDate).year()
    : "ц. ч.";

  return (
    <>
      {imagePath && (
        <StyledLeftContainer>
          <img
            src={`${process.env.PUBLIC_URL}/assets/${imagePath}`}
            style={{
              height: "100%",
              /*width: "100%",*/
              objectFit: "cover",
              borderRadius: "50%",
              border: `0.125rem solid ${fontColor}`,
              boxSizing: "border-box",
            }}
            alt="Изображение персоналии"
          />
        </StyledLeftContainer>
      )}
      <StyledRightContainer>
        <span id="number">{date.date()}</span>
        <span id="info">
          <p>
            {fullName
              .split(" ")
              .filter((_, index, array) => array.length < 3 || index !== 1)
              .join(" ")}
          </p>
          <p
            style={{
              fontVariantNumeric: "lining-nums",
            }}
          >
            ({birthYear} &#8211; {deathYear})
          </p>
        </span>
      </StyledRightContainer>
    </>
  );
};

type TableCellPlaceProps = {
  date: Dayjs;
  place: Place;
};

const TableCellPlace = ({ date, place }: TableCellPlaceProps) => {
  return (
    <StyledRightContainer>
      <span id="number">{date.date()}</span>
      <span id="info">
        <p>{place.name}</p>
      </span>
    </StyledRightContainer>
  );
};

type TableCellEventProps = {
  date: Dayjs;
  event: Event;
};

const TableCellEvent = ({ date, event }: TableCellEventProps) => {
  return (
    <StyledRightContainer>
      <span id="number">{date.date()}</span>
      <span id="info">
        <p>{event.name}</p>
      </span>
    </StyledRightContainer>
  );
};
